import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Award, TrendingUp, Save, AlertCircle, DollarSign } from 'lucide-react';
import { metricTracker } from '../../services/MetricTracker';
import { useAuth } from '../../contexts/AuthContext';

export default function PromotionModal({ contractor, onClose, onPromote }) {
    const { currentUser } = useAuth();
    const [adminNote, setAdminNote] = useState('');
    const [customRate, setCustomRate] = useState('');
    const [useCustomRate, setUseCustomRate] = useState(false);
    const [promoting, setPromoting] = useState(false);

    const currentPhase = contractor.phase || 'trainee';
    const nextPhase = contractor.metrics?.nextPhase;
    const isEligible = contractor.metrics?.eligibleForPromotion || false;

    const getPhaseInfo = (phase) => {
        const phases = {
            'shadow': { name: 'Shadow', rate: 0, color: 'gray' },
            'trainee': { name: 'Trainee', rate: 18, color: 'blue' },
            'junior': { name: 'Junior Tech', rate: 22, color: 'purple' },
            'standard': { name: 'Standard Tech', rate: 25, color: 'green' },
            'experienced': { name: 'Experienced Tech', rate: 27, color: 'yellow' }
        };
        return phases[phase] || phases['trainee'];
    };

    const currentPhaseInfo = getPhaseInfo(currentPhase);
    const nextPhaseInfo = nextPhase ? getPhaseInfo(nextPhase) : null;

    const handlePromote = async () => {
        if (!nextPhase) {
            alert('Contractor is already at the highest phase');
            return;
        }

        setPromoting(true);

        try {
            // Determine the rate
            const newRate = useCustomRate 
                ? parseFloat(customRate) 
                : nextPhaseInfo.rate;

            // Promote the contractor
            await metricTracker.promoteContractor(
                contractor.id,
                nextPhase,
                adminNote,
                currentUser.uid
            );

            // If custom rate, set it
            if (useCustomRate && customRate) {
                await metricTracker.setCustomRate(
                    contractor.id,
                    newRate,
                    'Custom rate set during promotion'
                );
            }

            alert(`✅ ${contractor.name} promoted to ${nextPhaseInfo.name}!`);

            if (onPromote) {
                onPromote();
            }

            onClose();

        } catch (error) {
            console.error('Error promoting contractor:', error);
            alert('Failed to promote contractor. Check console for details.');
        } finally {
            setPromoting(false);
        }
    };

    return (
        <AnimatePresence>
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
                <motion.div
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.95 }}
                    className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
                >
                    {/* Header */}
                    <div className="flex items-center justify-between p-6 border-b border-gray-200 sticky top-0 bg-white">
                        <div className="flex items-center space-x-3">
                            <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center">
                                <Award className="w-6 h-6 text-white" />
                            </div>
                            <div>
                                <h2 className="text-xl font-bold text-gray-900">
                                    Promote Contractor
                                </h2>
                                <p className="text-sm text-gray-600 mt-1">
                                    {contractor.name}
                                </p>
                            </div>
                        </div>
                        <button
                            onClick={onClose}
                            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                        >
                            <X className="w-5 h-5 text-gray-500" />
                        </button>
                    </div>

                    {/* Content */}
                    <div className="p-6 space-y-6">
                        {/* Current → Next Phase */}
                        <div className="flex items-center justify-center space-x-4">
                            {/* Current Phase */}
                            <div className="flex-1 text-center">
                                <div className={`mx-auto w-20 h-20 rounded-full bg-${currentPhaseInfo.color}-100 flex items-center justify-center mb-3`}>
                                    <TrendingUp className={`w-10 h-10 text-${currentPhaseInfo.color}-600`} />
                                </div>
                                <p className="text-sm text-gray-600">Current Phase</p>
                                <p className={`text-lg font-bold text-${currentPhaseInfo.color}-800`}>
                                    {currentPhaseInfo.name}
                                </p>
                                <p className="text-sm text-gray-500 mt-1">
                                    ${contractor.hourlyRate || currentPhaseInfo.rate}/hr
                                </p>
                            </div>

                            {/* Arrow */}
                            <div className="flex-shrink-0">
                                <svg className="w-8 h-8 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                                </svg>
                            </div>

                            {/* Next Phase */}
                            {nextPhaseInfo ? (
                                <div className="flex-1 text-center">
                                    <div className={`mx-auto w-20 h-20 rounded-full bg-${nextPhaseInfo.color}-100 flex items-center justify-center mb-3 ring-4 ring-${nextPhaseInfo.color}-200`}>
                                        <Award className={`w-10 h-10 text-${nextPhaseInfo.color}-600`} />
                                    </div>
                                    <p className="text-sm text-gray-600">Promote To</p>
                                    <p className={`text-lg font-bold text-${nextPhaseInfo.color}-800`}>
                                        {nextPhaseInfo.name}
                                    </p>
                                    <p className="text-sm text-gray-500 mt-1">
                                        ${nextPhaseInfo.rate}/hr
                                    </p>
                                </div>
                            ) : (
                                <div className="flex-1 text-center">
                                    <div className="mx-auto w-20 h-20 rounded-full bg-gray-100 flex items-center justify-center mb-3">
                                        <AlertCircle className="w-10 h-10 text-gray-400" />
                                    </div>
                                    <p className="text-sm text-gray-600">Already at</p>
                                    <p className="text-lg font-bold text-gray-800">
                                        Highest Phase
                                    </p>
                                </div>
                            )}
                        </div>

                        {/* Eligibility Status */}
                        {isEligible ? (
                            <div className="bg-green-50 border-2 border-green-200 rounded-lg p-4">
                                <div className="flex items-center space-x-3">
                                    <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center">
                                        <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                        </svg>
                                    </div>
                                    <div className="flex-1">
                                        <p className="font-bold text-green-900">Eligible for Promotion</p>
                                        <p className="text-sm text-green-700 mt-1">
                                            All criteria have been met for advancement to {nextPhaseInfo?.name}.
                                        </p>
                                    </div>
                                </div>
                            </div>
                        ) : (
                            <div className="bg-yellow-50 border-2 border-yellow-200 rounded-lg p-4">
                                <div className="flex items-center space-x-3">
                                    <AlertCircle className="w-8 h-8 text-yellow-600" />
                                    <div className="flex-1">
                                        <p className="font-bold text-yellow-900">Manual Override Required</p>
                                        <p className="text-sm text-yellow-700 mt-1">
                                            Criteria not fully met. You can still promote with admin discretion.
                                        </p>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Criteria Status */}
                        {contractor.metrics?.criteriaStatus && (
                            <div>
                                <h3 className="font-semibold text-gray-900 mb-3">Promotion Criteria</h3>
                                <div className="space-y-2">
                                    {Object.keys(contractor.metrics.criteriaStatus).map((key) => {
                                        const status = contractor.metrics.criteriaStatus[key];
                                        const isMet = status.met;

                                        return (
                                            <div key={key} className="flex items-center justify-between text-sm">
                                                <span className="text-gray-700 capitalize">
                                                    {key.replace(/([A-Z])/g, ' $1').trim()}
                                                </span>
                                                <div className="flex items-center space-x-2">
                                                    <span className={`font-medium ${isMet ? 'text-green-600' : 'text-gray-600'}`}>
                                                        {status.current} / {status.required}
                                                    </span>
                                                    {isMet ? (
                                                        <span className="text-green-600">✓</span>
                                                    ) : (
                                                        <span className="text-gray-400">✗</span>
                                                    )}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        )}

                        {/* Custom Rate Option */}
                        <div className="border-t border-gray-200 pt-6">
                            <div className="flex items-start space-x-3 mb-3">
                                <input
                                    type="checkbox"
                                    checked={useCustomRate}
                                    onChange={(e) => setUseCustomRate(e.target.checked)}
                                    className="mt-1 w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                />
                                <div className="flex-1">
                                    <label className="font-semibold text-gray-900 cursor-pointer">
                                        Set Custom Hourly Rate
                                    </label>
                                    <p className="text-sm text-gray-600 mt-1">
                                        Override the default rate for this contractor's new phase.
                                    </p>
                                </div>
                            </div>

                            {useCustomRate && (
                                <div className="ml-8">
                                    <div className="relative">
                                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                            <DollarSign className="w-5 h-5 text-gray-400" />
                                        </div>
                                        <input
                                            type="number"
                                            step="0.5"
                                            min="0"
                                            value={customRate}
                                            onChange={(e) => setCustomRate(e.target.value)}
                                            placeholder={nextPhaseInfo?.rate.toString() || '0'}
                                            className="block w-full pl-10 pr-12 border border-gray-300 rounded-lg py-2 focus:ring-2 focus:ring-blue-500 outline-none"
                                        />
                                        <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                                            <span className="text-gray-500 text-sm">/hr</span>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Admin Note */}
                        <div>
                            <label className="block font-semibold text-gray-900 mb-2">
                                Promotion Note (Optional)
                            </label>
                            <textarea
                                value={adminNote}
                                onChange={(e) => setAdminNote(e.target.value)}
                                placeholder="e.g., Exceptional performance on recent projects, consistently exceeds expectations..."
                                rows={3}
                                className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-blue-500 outline-none"
                            />
                            <p className="text-xs text-gray-500 mt-1">
                                This note will be saved in the promotion history.
                            </p>
                        </div>
                    </div>

                    {/* Footer */}
                    <div className="flex items-center justify-end space-x-3 p-6 border-t border-gray-200 bg-gray-50">
                        <button
                            onClick={onClose}
                            className="px-4 py-2 text-gray-700 hover:bg-gray-200 rounded-lg transition-colors"
                        >
                            Cancel
                        </button>
                        <button
                            onClick={handlePromote}
                            disabled={promoting || !nextPhase}
                            className="flex items-center space-x-2 px-6 py-2 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg hover:from-blue-700 hover:to-purple-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {promoting ? (
                                <>
                                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                                    <span>Promoting...</span>
                                </>
                            ) : (
                                <>
                                    <Award className="w-4 h-4" />
                                    <span>Promote to {nextPhaseInfo?.name || 'Next Phase'}</span>
                                </>
                            )}
                        </button>
                    </div>
                </motion.div>
            </div>
        </AnimatePresence>
    );
}
